import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImageIcon, Send, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const inputCls = "bg-surface-2/60 border-metallic text-white placeholder:text-slate-500";

export default function VehicleSubmissionForm({ compact = false }) {
  const fileRef = useRef(null);
  const [form, setForm] = useState({ name: "", phone: "", email: "", regnummer: "", info: "" });
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [sending, setSending] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const pickFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length + files.length > 6) {
      toast.error("Max 6 foton per inlämning.");
      return;
    }
    const next = [...files, ...picked].slice(0, 6);
    setFiles(next);
    setPreviews(next.map((f) => URL.createObjectURL(f)));
  };

  const removeFile = (i) => {
    const next = files.filter((_, idx) => idx !== i);
    setFiles(next);
    setPreviews(next.map((f) => URL.createObjectURL(f)));
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("phone", form.phone);
      if (form.email) fd.append("email", form.email);
      fd.append("regnummer", form.regnummer);
      if (form.info) fd.append("info", form.info);
      files.forEach((f) => fd.append("photos", f));
      const { data } = await api.post("/submit-vehicle", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.email_sent) {
        toast.success("Tack! Vi har fått din inlämning och hör av oss så snart vi kan.");
      } else {
        toast.success("Tack! Din inlämning är mottagen. Hör du inte av oss, ring oss gärna.");
      }
      setForm({ name: "", phone: "", email: "", regnummer: "", info: "" });
      setFiles([]);
      setPreviews([]);
      if (fileRef.current) fileRef.current.value = "";
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className={`rounded-3xl border border-metallic/70 bg-surface ${compact ? "p-6 md:p-8" : "p-8"}`}
      data-testid="salj-form"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="salj-name" className="text-slate-300">Namn *</Label>
          <Input
            id="salj-name"
            data-testid="salj-name-input"
            required
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Ditt namn"
            className={inputCls}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="salj-phone" className="text-slate-300">Telefon *</Label>
          <Input
            id="salj-phone"
            data-testid="salj-phone-input"
            required
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="07X-XXX XX XX"
            className={inputCls}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="salj-email" className="text-slate-300">E-post (valfritt)</Label>
          <Input
            id="salj-email"
            data-testid="salj-email-input"
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="din@epost.se"
            className={inputCls}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="salj-reg" className="text-slate-300">Regnummer *</Label>
          <Input
            id="salj-reg"
            data-testid="salj-reg-input"
            required
            value={form.regnummer}
            onChange={(e) => set("regnummer", e.target.value.toUpperCase())}
            placeholder="ABC 123"
            className={`${inputCls} font-mono-brand uppercase`}
          />
        </div>
      </div>

      <div className="mt-4 space-y-1.5">
        <Label className="text-slate-300">Foton (valfritt, max 6)</Label>
        <button
          type="button"
          data-testid="salj-photos-button"
          onClick={() => fileRef.current?.click()}
          className="w-full rounded-xl border border-dashed border-metallic bg-surface-2/40 px-4 py-6 text-sm text-slate-400 hover:border-brand/60 hover:text-slate-300 transition-colors flex items-center justify-center gap-2"
        >
          <ImageIcon size={16} className="text-electric" />
          Klicka för att välja bilder
        </button>
        <input
          ref={fileRef}
          data-testid="salj-photos-input"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/avif,image/bmp,image/tiff,image/heic,image/heif"
          multiple
          className="hidden"
          onChange={pickFiles}
        />
        {previews.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mt-3" data-testid="salj-photo-previews">
            {previews.map((src, i) => (
              <div key={i} className="relative rounded-lg overflow-hidden border border-metallic">
                <img src={src} alt={`Foto ${i + 1}`} className="aspect-[4/3] w-full object-cover" data-testid={`salj-photo-thumb-${i}`} />
                <button
                  type="button"
                  data-testid={`salj-photo-remove-${i}`}
                  onClick={() => removeFile(i)}
                  className="absolute top-1.5 right-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-ink/90 text-slate-300 hover:text-white"
                  aria-label="Ta bort foto"
                >
                  <X size={13} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-4 space-y-1.5">
        <Label htmlFor="salj-info" className="text-slate-300">Info om fordonet (valfritt)</Label>
        <Textarea
          id="salj-info"
          data-testid="salj-info-input"
          rows={5}
          value={form.info}
          onChange={(e) => set("info", e.target.value)}
          placeholder="T.ex. årsmodell, mätarställning, skick, utrustning och om du har serviceunderlag..."
          className={inputCls}
        />
      </div>

      <Button
        type="submit"
        data-testid="salj-submit-button"
        disabled={sending}
        className="mt-6 w-full rounded-full bg-brand hover:bg-brand-dark text-white font-semibold py-3"
      >
        <Send size={16} className="mr-2" />
        {sending ? "Skickar..." : "Skicka in ditt fordon"}
      </Button>
    </form>
  );
}
